import React, { useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  CardMedia,
  InputAdornment,
  FormHelperText,
  Alert,
  Paper,
  Divider,
  CircularProgress
} from '@mui/material';
import { Calendar, DollarSign, Target, Image as ImageIcon, CheckCircle } from 'lucide-react';

const steps = ['Informações Básicas', 'Segmentação', 'Orçamento e Programação', 'Criativos', 'Revisão'];

const initialFormData = {
  // Step 1: Informações Básicas
  name: '',
  objective: '',
  description: '',
  
  // Step 2: Segmentação
  ageMin: '18',
  ageMax: '65',
  gender: 'all',
  locations: [],
  interests: [],
  
  // Step 3: Orçamento e Programação
  budgetType: 'daily',
  budget: '',
  startDate: '',
  endDate: '',
  
  // Step 4: Criativos
  selectedCreatives: []
};

const objectives = [
  { value: 'awareness', label: 'Reconhecimento de Marca' },
  { value: 'traffic', label: 'Tráfego' },
  { value: 'engagement', label: 'Engajamento' },
  { value: 'leads', label: 'Geração de Leads' },
  { value: 'conversions', label: 'Conversões' },
  { value: 'sales', label: 'Vendas' }
];

const genderOptions = [
  { value: 'all', label: 'Todos' },
  { value: 'male', label: 'Masculino' },
  { value: 'female', label: 'Feminino' }
];

const popularInterests = [
  'Tecnologia', 'Moda', 'Esportes', 'Viagens', 'Gastronomia',
  'Fitness', 'Música', 'Cinema', 'Games', 'Negócios',
  'Educação', 'Saúde', 'Beleza', 'Automóveis', 'Pets'
];

// Mock de criativos disponíveis
const mockCreatives = [
  { id: 1, name: 'Banner Principal', url: 'https://via.placeholder.com/400x300/4CAF50/white?text=Banner+1', type: 'image' },
  { id: 2, name: 'Promoção Verão', url: 'https://via.placeholder.com/400x300/2196F3/white?text=Banner+2', type: 'image' },
  { id: 3, name: 'Lançamento Produto', url: 'https://via.placeholder.com/400x300/FF9800/white?text=Banner+3', type: 'image' },
  { id: 4, name: 'Oferta Especial', url: 'https://via.placeholder.com/400x300/E91E63/white?text=Banner+4', type: 'image' }
];

function CampaignForm() {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [newLocation, setNewLocation] = useState('');
  const [newInterest, setNewInterest] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Limpar erro do campo ao editar
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    // Step 1 - Informações Básicas
    if (step === 0) {
      // Valida nome vazio
      if (!formData.name.trim()) {
        newErrors.name = 'Nome é obrigatório';
      }
      // Valida nome muito curto (< 3 caracteres)
      else if (formData.name.trim().length < 3) {
        newErrors.name = 'O nome deve ter pelo menos 3 caracteres';
      }
      // Valida nome muito longo (> 100 caracteres)
      else if (formData.name.trim().length > 100) {
        newErrors.name = 'O nome não pode ter mais de 100 caracteres';
      }

      // Valida objetivo obrigatório
      if (!formData.objective) {
        newErrors.objective = 'Selecione um objetivo para a campanha';
      }
    }

    // Step 2 - Segmentação
    if (step === 1) {
      const ageMin = parseInt(formData.ageMin);
      const ageMax = parseInt(formData.ageMax);

      // Valida faixa etária (mínimo 13, máximo 65 anos)
      if (ageMin < 13 || ageMin > 65) {
        newErrors.ageMin = 'A idade mínima deve estar entre 13 e 65 anos';
      }
      if (ageMax < 13 || ageMax > 65) {
        newErrors.ageMax = 'A idade máxima deve estar entre 13 e 65 anos';
      }

      // Valida se idade mínima é menor que máxima
      if (ageMin > ageMax) {
        newErrors.ageMin = 'A idade mínima deve ser menor que a máxima';
      }

      // Recomenda faixa de pelo menos 5 anos
      if (ageMax - ageMin < 5) {
        newErrors.ageMax = 'Recomendamos uma faixa etária de pelo menos 5 anos para melhor alcance';
      }

      // Valida localizações obrigatórias
      if (formData.locations.length === 0) {
        newErrors.locations = 'Adicione pelo menos uma localização para segmentar sua campanha';
      }

      // Limita localizações em 50
      if (formData.locations.length > 50) {
        newErrors.locations = 'Você pode adicionar no máximo 50 localizações';
      }

      // Sugere adicionar interesses (não obrigatório, mas recomendado)
      if (formData.interests.length === 0) {
        newErrors.interests = 'Recomendamos adicionar pelo menos um interesse para melhorar a segmentação';
      }
    }

    // Step 3 - Orçamento e Programação
    if (step === 2) {
      const budget = parseFloat(formData.budget);

      // Valida orçamento vazio ou inválido
      if (!formData.budget) {
        newErrors.budget = 'Orçamento é obrigatório';
      }
      // Valida valores mínimos (R$ 5)
      else if (budget < 5) {
        newErrors.budget = 'O orçamento mínimo é de R$ 5,00';
      }
      // Valida valores máximos (R$ 1.000.000)
      else if (budget > 1000000) {
        newErrors.budget = 'O orçamento não pode exceder R$ 1.000.000,00';
      }

      // Valida data de início obrigatória
      if (!formData.startDate) {
        newErrors.startDate = 'Data de início é obrigatória';
      } else {
        // Não permite datas passadas
        const today = new Date().toISOString().split('T')[0];
        if (formData.startDate < today) {
          newErrors.startDate = 'A data de início não pode ser no passado';
        }
      }

      // Valida data de término obrigatória
      if (!formData.endDate) {
        newErrors.endDate = 'Data de término é obrigatória';
      }

      // Valida se data de término é posterior à data de início
      if (formData.startDate && formData.endDate) {
        if (formData.startDate >= formData.endDate) {
          newErrors.endDate = 'A data de término deve ser posterior à data de início';
        }

        // Valida duração da campanha (mínimo 1 dia, máximo 365 dias)
        const days = Math.ceil((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24));
        
        if (days < 1) {
          newErrors.endDate = 'A campanha deve ter pelo menos 1 dia de duração';
        }
        if (days > 365) {
          newErrors.endDate = 'A campanha não pode ter mais de 365 dias de duração';
        }

        // Alerta quando orçamento diário fica muito baixo
        if (formData.budgetType === 'total' && budget) {
          const dailyBudget = budget / days;
          
          if (dailyBudget < 5) {
            newErrors.budget = `Com essa duração, o orçamento diário ficaria R$ ${dailyBudget.toFixed(2)}, abaixo do mínimo de R$ 5,00. Aumente o orçamento total ou reduza a duração.`;
          }
        }
      }
    }

    // Step 4 - Criativos
    if (step === 3) {
      // Valida seleção obrigatória de criativos
      if (formData.selectedCreatives.length === 0) {
        newErrors.selectedCreatives = 'Selecione pelo menos um criativo para a campanha';
      }
      
      // Limita seleção em 10 criativos
      if (formData.selectedCreatives.length > 10) {
        newErrors.selectedCreatives = 'Você pode selecionar no máximo 10 criativos por campanha';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddLocation = () => {
    if (newLocation.trim() && !formData.locations.includes(newLocation.trim())) {
      handleChange('locations', [...formData.locations, newLocation.trim()]);
      setNewLocation('');
    }
  };

  const handleRemoveLocation = (location) => {
    handleChange('locations', formData.locations.filter((l) => l !== location));
  };

  const handleAddInterest = (interest) => {
    if (!formData.interests.includes(interest)) {
      handleChange('interests', [...formData.interests, interest]);
    }
  };

  const handleRemoveInterest = (interest) => {
    handleChange('interests', formData.interests.filter((i) => i !== interest));
  };

  const handleToggleCreative = (creativeId) => {
    const isSelected = formData.selectedCreatives.includes(creativeId);
    if (isSelected) {
      handleChange('selectedCreatives', formData.selectedCreatives.filter((id) => id !== creativeId));
    } else {
      handleChange('selectedCreatives', [...formData.selectedCreatives, creativeId]);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      // Simula requisição com delay (substitua pela sua API real)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Submitting campaign:', formData);
      alert('Campanha criada com sucesso!');
      // Aqui você faria a requisição para o backend
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Informações Básicas da Campanha
            </Typography>
            
            <TextField
              fullWidth
              label="Nome da Campanha"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ mb: 3 }}
              required
              disabled={isLoading}
            />

            <FormControl fullWidth sx={{ mb: 3 }} error={!!errors.objective} required>
              <InputLabel>Objetivo da Campanha</InputLabel>
              <Select
                value={formData.objective}
                onChange={(e) => handleChange('objective', e.target.value)}
                label="Objetivo da Campanha"
                disabled={isLoading}
              >
                {objectives.map((obj) => (
                  <MenuItem key={obj.value} value={obj.value}>
                    {obj.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.objective && <FormHelperText>{errors.objective}</FormHelperText>}
            </FormControl>

            <TextField
              fullWidth
              label="Descrição"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              multiline
              rows={4}
              helperText="Descreva os objetivos e detalhes da campanha"
              disabled={isLoading}
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Segmentação de Público
            </Typography>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Idade Mínima"
                  value={formData.ageMin}
                  onChange={(e) => handleChange('ageMin', e.target.value)}
                  error={!!errors.ageMin}
                  helperText={errors.ageMin}
                  inputProps={{ min: 13, max: 65 }}
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Idade Máxima"
                  value={formData.ageMax}
                  onChange={(e) => handleChange('ageMax', e.target.value)}
                  error={!!errors.ageMax}
                  helperText={errors.ageMax}
                  inputProps={{ min: 13, max: 65 }}
                  disabled={isLoading}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Gênero</InputLabel>
              <Select
                value={formData.gender}
                onChange={(e) => handleChange('gender', e.target.value)}
                label="Gênero"
                disabled={isLoading}
              >
                {genderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Localizações *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Ex: São Paulo, Brasil"
                  value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddLocation()}
                  error={!!errors.locations}
                  disabled={isLoading}
                />
                <Button 
                  variant="outlined" 
                  onClick={handleAddLocation}
                  disabled={isLoading}
                >
                  Adicionar
                </Button>
              </Box>
              {errors.locations && (
                <Typography color="error" variant="caption">
                  {errors.locations}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.locations.map((location) => (
                  <Chip
                    key={location}
                    label={location}
                    onDelete={() => handleRemoveLocation(location)}
                    disabled={isLoading}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Interesses
              </Typography>
              {errors.interests && (
                <Typography color="warning.main" variant="caption" sx={{ display: 'block', mb: 1 }}>
                  {errors.interests}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {popularInterests.map((interest) => (
                  <Chip
                    key={interest}
                    label={interest}
                    onClick={() => handleAddInterest(interest)}
                    color={formData.interests.includes(interest) ? 'primary' : 'default'}
                    variant={formData.interests.includes(interest) ? 'filled' : 'outlined'}
                    disabled={isLoading}
                  />
                ))}
              </Box>
              {formData.interests.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Selecionados:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {formData.interests.map((interest) => (
                      <Chip
                        key={interest}
                        label={interest}
                        onDelete={() => handleRemoveInterest(interest)}
                        color="primary"
                        disabled={isLoading}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Orçamento e Programação
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Tipo de Orçamento</InputLabel>
              <Select
                value={formData.budgetType}
                onChange={(e) => handleChange('budgetType', e.target.value)}
                label="Tipo de Orçamento"
                disabled={isLoading}
              >
                <MenuItem value="daily">Diário</MenuItem>
                <MenuItem value="total">Total da Campanha</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label={`Orçamento ${formData.budgetType === 'daily' ? 'Diário' : 'Total'}`}
              value={formData.budget}
              onChange={(e) => handleChange('budget', e.target.value)}
              error={!!errors.budget}
              helperText={errors.budget}
              InputProps={{
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              }}
              sx={{ mb: 3 }}
              required
              disabled={isLoading}
            />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de Início"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().split('T')[0] }}
                  required
                  disabled={isLoading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de Término"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: formData.startDate || new Date().toISOString().split('T')[0] }}
                  required
                  disabled={isLoading}
                />
              </Grid>
            </Grid>

            {formData.budget && formData.startDate && formData.endDate && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Estimativa:</strong>
                  {formData.budgetType === 'daily' ? (
                    <> Orçamento total aproximado: R$ {(parseFloat(formData.budget) * Math.ceil((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24))).toFixed(2)}</>
                  ) : (
                    <> Orçamento diário aproximado: R$ {(parseFloat(formData.budget) / Math.ceil((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24))).toFixed(2)}</>
                  )}
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Selecione os Criativos
            </Typography>
            {errors.selectedCreatives && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.selectedCreatives}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              {mockCreatives.map((creative) => {
                const isSelected = formData.selectedCreatives.includes(creative.id);
                return (
                  <Grid item xs={12} sm={6} md={4} key={creative.id}>
                    <Card
                      sx={{
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        border: isSelected ? '2px solid' : '2px solid transparent',
                        borderColor: isSelected ? 'primary.main' : 'transparent',
                        position: 'relative',
                        transition: 'all 0.3s',
                        opacity: isLoading ? 0.6 : 1
                      }}
                      onClick={() => !isLoading && handleToggleCreative(creative.id)}
                    >
                      {isSelected && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            bgcolor: 'primary.main',
                            borderRadius: '50%',
                            p: 0.5
                          }}
                        >
                          <CheckCircle size={20} color="white" />
                        </Box>
                      )}
                      <CardMedia
                        component="img"
                        height="200"
                        image={creative.url}
                        alt={creative.name}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent>
                        <Typography variant="body2">
                          {creative.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Revisão da Campanha
            </Typography>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Informações Básicas</strong>
              </Typography>
              <Typography variant="body2"><strong>Nome:</strong> {formData.name}</Typography>
              <Typography variant="body2">
                <strong>Objetivo:</strong> {objectives.find(o => o.value === formData.objective)?.label}
              </Typography>
              {formData.description && (
                <Typography variant="body2"><strong>Descrição:</strong> {formData.description}</Typography>
              )}
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Segmentação</strong>
              </Typography>
              <Typography variant="body2">
                <strong>Idade:</strong> {formData.ageMin} - {formData.ageMax} anos
              </Typography>
              <Typography variant="body2">
                <strong>Gênero:</strong> {genderOptions.find(g => g.value === formData.gender)?.label}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Localizações:</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {formData.locations.map(loc => (
                  <Chip key={loc} label={loc} size="small" />
                ))}
              </Box>
              {formData.interests.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    <strong>Interesses:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {formData.interests.map(int => (
                      <Chip key={int} label={int} size="small" color="primary" />
                    ))}
                  </Box>
                </>
              )}
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Orçamento e Programação</strong>
              </Typography>
              <Typography variant="body2">
                <strong>Tipo:</strong> {formData.budgetType === 'daily' ? 'Diário' : 'Total'}
              </Typography>
              <Typography variant="body2">
                <strong>Orçamento:</strong> R$ {parseFloat(formData.budget).toFixed(2)}
              </Typography>
              <Typography variant="body2">
                <strong>Período:</strong> {new Date(formData.startDate).toLocaleDateString('pt-BR')} até {new Date(formData.endDate).toLocaleDateString('pt-BR')}
              </Typography>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                <strong>Criativos Selecionados ({formData.selectedCreatives.length})</strong>
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {mockCreatives
                  .filter(c => formData.selectedCreatives.includes(c.id))
                  .map(creative => (
                    <Grid item xs={6} sm={4} md={3} key={creative.id}>
                      <Card>
                        <CardMedia
                          component="img"
                          height="120"
                          image={creative.url}
                          alt={creative.name}
                        />
                        <CardContent sx={{ p: 1 }}>
                          <Typography variant="caption">{creative.name}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Nova Campanha
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 4, mb: 3 }}>
        {renderStepContent(activeStep)}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0 || isLoading}
          onClick={handleBack}
          variant="outlined"
        >
          Voltar
        </Button>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              size="large"
              disabled={isLoading}
            >
              {isLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  Criando Campanha...
                </Box>
              ) : (
                'Criar Campanha'
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              size="large"
              disabled={isLoading}
            >
              Próximo
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default CampaignForm;